import type { EntryPoint, EntryPointComponent } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { PlatformSiteAri } from '@atlassian/ari/platform';
import { JSResourceForInteraction } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';

import AgentStudioSkillsQuery from './ui/__generated__/AgentStudioSkillsQuery.graphql';
import type { AgentStudioSkillsProps, AgentStudioSkillsQueries } from './ui/AgentStudioSkills';

export type { AgentStudioSkillsCapability, AgentStudioSkillsProps } from './ui/AgentStudioSkills';

type AgentStudioSkillsEntrypointComponent = EntryPointComponent<
	AgentStudioSkillsQueries,
	Record<string, never>,
	Omit<AgentStudioSkillsProps, 'queryRef'>,
	Partial<Omit<AgentStudioSkillsProps, 'queryRef'>>
>;

export const agentStudioSkillsEntrypoint: EntryPoint<
	AgentStudioSkillsEntrypointComponent,
	{
		capabilities?: AgentStudioSkillsProps['capabilities'];
		cloudId: string;
		externalAllowlistedSkillProducts?: AgentStudioSkillsProps['externalAllowlistedSkillProducts'];
		onSkillClick?: AgentStudioSkillsProps['onSkillClick'];
	}
> = createEntryPoint({
	root: JSResourceForInteraction<AgentStudioSkillsEntrypointComponent>(
		/* webpackChunkName: "@atlaskit-internal_agent-studio-skills-entrypoint" */ './ui/AgentStudioSkills',
		{
			moduleId: 'agent-studio-skills-entrypoint-id',
			moduleName: 'agent-studio-skills-entrypoint',
		},
	),
	getPreloadProps: ({
		capabilities,
		cloudId,
		onSkillClick,
		externalAllowlistedSkillProducts,
	}: {
		capabilities?: AgentStudioSkillsProps['capabilities'];
		cloudId: string;
		externalAllowlistedSkillProducts?: AgentStudioSkillsProps['externalAllowlistedSkillProducts'];
		onSkillClick?: AgentStudioSkillsProps['onSkillClick'];
	}) => {
		return {
			queries: {
				skillsQuery: {
					parameters: AgentStudioSkillsQuery,
					variables: {
						cloudId,
						contextAri: PlatformSiteAri.create({ siteId: cloudId }).toString(),
						isStudioCustomSkillsM1FrontendEnabled: fg('studio_custom_skills_m1_frontend'),
					},
					options: {
						fetchPolicy: 'store-or-network' as const,
					},
				},
			},
			extraProps: {
				capabilities,
				cloudId,
				onSkillClick,
				externalAllowlistedSkillProducts,
			},
		};
	},
});
